<template>
  <div
    ref="videoPlayer"
    class="video-player"
  >
    <transition name="fade">
      <button
        v-if="showOverlay"
        class="video-player__overlay"
        type="button"
        :aria-label="controlText"
        @click="handleOverlayClick"
      >
        <slot name="image">
          <responsive-image
            v-if="activeVideo.previewImage"
            :key="activeVideo.id"
            class="video-player__image"
            :image="activeVideo.previewImage"
            min-max="300-720"
            :padding="false"
            :use-image-loading="false"
            :sizes="previewSize"
          />
        </slot>
      </button>
    </transition>

    <div
      class="video-player__controls"
      :class="`video-player__controls--${controlsAlignment}`"
    >
      <transition name="fade">
        <button
          v-if="uiConfig.controls || displayControls"
          class="video-player__control"
          type="button"
          @click="handleControlClick"
        >
          <span
            class="visually-hidden"
            v-text="controlText"
          />

          <component :is="icon" />
        </button>
      </transition>

      <transition name="fade">
        <button
          v-if="uiConfig.volume"
          class="video-player__control"
          type="button"
          @click="muted = !muted"
        >
          <span
            class="visually-hidden"
            v-text="
              muted ? $string('accessibility.video.unmute') : $string('accessibility.video.mute')
            "
          />

          <component :is="volumeIcon" />
        </button>
      </transition>

      <button
        v-if="subtitles"
        class="video-player__subtitles"
        :class="{ [$class('active')]: displaySubtitles }"
        type="button"
        @click="displaySubtitles = !displaySubtitles"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.video.subtitles')"
        />

        <span
          aria-hidden="true"
          v-text="$string('accessibility.video.cc')"
        />
      </button>
    </div>

    <video
      ref="video"
      :key="activeVideo.id"
      class="video-player__video"
      :class="{
        'video-player__video--no-controls': !displayControls,
      }"
      controlslist="nofullscreen"
      crossorigin="anonymous"
      disablepictureinpicture
      loop
      :muted="muted ? true : undefined"
      playsinline
      preload="none"
      :aria-label="controlText"
      @click="handleControlClick"
      @keyup.enter="handleControlClick"
      @timeupdate="handleVideoTimeUpdate"
      @pause="state = 'paused'"
      @play="state = 'playing'"
      @canplay="setReadyState"
      @canplaythrough="setReadyState"
      @loadedmetadata="setReadyState"
    >
      <source
        v-for="source of activeVideoSources"
        :key="source.url"
        :src="source.url"
        :type="source.mimeType"
      >

      <track
        v-if="subtitles"
        kind="subtitles"
        label="English"
        :src="subtitles"
        srclang="en"
      >
    </video>

    <slot />
  </div>
</template>

<script>
/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * Lookbook video player functionality.
 *
 * @param {String} [controlsAlignment] - Position of play/pause controls,
 * accepts 'left', 'right', 'top-right', 'top-left', or 'center'.
 * @param {Boolean} [isActive] - Slide containing video is active.
 * @param {Boolean} [isAutoplay] - Use autoplay automatically.
 * @param {String} [subtitles] - Video subtitles URL.
 * @param {{previewImage: Object, sources: Array}} video
 * @param {{url: String, mimeType: String}[]} video.sources - Video sources
 * @param {{previewImage: Object, sources: Array}} mobileVideo
 * @param {{url: String, mimeType: String}[]} mobileVideo.sources - Mobile video
 * sources
 * @param {Boolean} [ui.controls] - Display playback controls (play /
 * pause / loading)
 * @param {Boolean} [ui.volume] - Display mute / unmute
 * @param {false} [ui.powerSavingControls] - Use false to disable playback
 * controls in iOS power saving mode.
 * @param {String} [overlayNamespace] - If the video is being rendered in an overlay, provide the
 * overlay's namespace to automatically pause videos when the overlay is not in view
 *
 */
import { mapGetters, mapState } from 'vuex'
import { setIntersectionObserver } from '~/helpers/general'

import ResponsiveImage from '~global/images/responsive-image'

import IconLoading from '~icons/general/loading.svg'
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'
import IconVolumeOff from '~icons/general/volume-off.svg'
import IconVolumeOn from '~icons/general/volume-on.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconLoading,
    IconPause,
    IconPlay,
    IconVolumeOff,
    IconVolumeOn,
    ResponsiveImage,
  },

  props: {
    controlsAlignment: {
      type: String,
      default: 'left',
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isAutoplay: {
      type: Boolean,
      default: false,
    },
    mobileVideo: {
      type: Object,
      default: null,
    },
    subtitles: {
      type: String,
      default: '',
    },
    ui: {
      type: Object,
      default: () => ({}),
    },
    video: {
      type: Object,
      default: () => ({
        previewImage: null,
        sources: [],
      }),
      required: true,
    },
    volume: {
      type: Number,
      default: 0,
    },
    previewSize: {
      type: String,
      default: '100vw',
    },
    overlayNamespace: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      displaySubtitles: false,
      muted: true,
      observer: null,
      overlayPaused: false,
      played: false,
      showOverlay: true,
      state: this.isAutoplay ? 'loading' : 'paused',
      time: 0,
      uiConfig: {
        volume: false,
        controls: false,
        ...this.ui,
      },
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute active video
     * @returns {Object}
     */
    activeVideo() {
      return this.isMobile && this.mobileVideo ? this.mobileVideo : this.video
    },

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    controlText() {
      switch (this.state) {
        case 'loading':
          return this.$string('accessibility.video.loading')

        case 'playing':
          return this.$string('accessibility.video.pause')

        case 'paused':
        default:
          return this.$string('accessibility.video.play')
      }
    },

    /**
     * Compute if controls should display
     * @returns {boolean}
     */
    displayControls() {
      return (
        this.uiConfig.controls ||
        (this.state === 'paused' && !this.played && this.ui.powerSavingControls !== false)
      )
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    icon() {
      switch (this.state) {
        case 'loading':
          return 'icon-loading'

        case 'playing':
          return 'icon-pause'

        case 'paused':
        default:
          return 'icon-play'
      }
    },

    /**
     * Filter any unneeded sources.
     */
    activeVideoSources() {
      return this.activeVideo.sources.filter(
        (source) => source.mimeType !== 'application/x-mpegURL',
      )
    },

    volumeIcon() {
      return this.muted ? 'icon-volume-off' : 'icon-volume-on'
    },
  },

  watch: {
    /**
     * Re-initialize video if active video changes
     */
    async 'activeVideo.id'() {
      await this.$nextTick()
      this.init()
    },

    /**
     * Watch display subtitles to show track.
     */
    displaySubtitles() {
      if (!this.displaySubtitles) {
        this.$refs.video.textTracks[0].mode = 'hidden'
        return
      }

      this.$refs.video.textTracks[0].mode = 'showing'
    },

    /**
     * Toggle play/pause states when an overlay is toggled (except for concierge menu)
     */
    getActiveOverlay(overlay) {
      if (overlay?.namespace === 'conciergeOverlay' && !this.isMobile) {
        return
      }

      // If a namespace was provided, play the video if the active overlay matches
      // Otherwise, pause the video
      if (this.overlayNamespace) {
        if (this.overlayNamespace === overlay?.namespace) {
          this.handleAutoplay()
        } else {
          this.pauseVideo()
        }
        return
      }

      // Pause video when an overlay is opened
      if (overlay) {
        if (this.state === 'playing') {
          this.pauseVideo()
          this.overlayPaused = true
          return
        }

        this.overlayPaused = false
        return
      }

      // If a video was paused as a result of opening an overlay, resume the video once the overlay
      // is closed
      if (this.overlayPaused) {
        this.playVideo()
      }
    },

    /**
     * Watch active state to play video when slide containing it becomes active.
     * - Don't autoplay if prefers reduced motion.
     */
    isActive() {
      this.handleAutoplay()
    },

    /**
     * Disable volume when muted
     */
    muted() {
      this.$refs.video.volume = this.muted ? 0 : this.volume
    },
  },

  mounted() {
    this.init()
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },

  methods: {
    /**
     * Initialize on mount
     */
    init() {
      if (!this.$refs.video) {
        return
      }

      this.$refs.video.volume = this.muted ? 0 : this.volume

      /**
       * Reset any subtitles that browser has enabled by default.
       */
      if (!this.$refs.video.textTracks.length) {
        Object.keys(this.$refs.video.textTracks).forEach((_, index) => {
          this.$refs.video.textTracks[index].mode = 'hidden'
        })
      }

      /**
       * Set intersection observer so that handleAutoplay() only runs when video
       * is in view.
       */
      this.setVideoIntersectionObserver()
    },

    /**
     * Handle autoplay.
     */
    handleAutoplay() {
      if (
        (this.isActive || this.isAutoplay) &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.playVideo()
        return
      }

      /**
       * Prevents preview image not displaying & the play/pause icon being
       * stuck on the loading icon when user prefers reduced motion.
       * - Only run if prefers reduced motion to prevent DOMException errors.
       */
      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        this.state === 'loading'
      ) {
        this.$refs.video.load()
      }

      this.pauseVideo()
    },

    /**
     * Handle overlay click.
     */
    handleOverlayClick() {
      if (this.state === 'loading' || !this.displayControls) {
        return
      }

      this.playVideo()
    },

    /**
     * Handle control and video click.
     */
    handleControlClick() {
      if (!this.displayControls) {
        return
      }

      if (this.state === 'playing') {
        this.pauseVideo()
        return
      }

      this.playVideo()
    },

    /**
     * Play video.
     */
    async playVideo() {
      /**
       * Addtional check that the video exists (useful when page changes
       * are handled via Vue, e.g. PLPs).
       */
      if (!this.$refs.video) {
        return
      }

      /**
       * Prevents following error:
       * DOMException: The play() request was interrupted by a call to pause()
       */
      if (this.state === 'playing' && !this.$refs.video.paused) {
        return
      }

      try {
        this.$refs.video.autoplay = true
        await this.$refs.video.play().catch()
        this.showOverlay = false
        this.played = true

        if (this.observer) {
          this.observer.disconnect()
          this.observer = null
        }
      } catch (error) {
        if (error?.name === 'NotAllowedError') {
          this.state = 'paused'
        }
        cnvs.ReportError('Failed to play video', error)
      }
    },

    /**
     * Pause video.
     */
    async pauseVideo() {
      if (!this.$refs.video) {
        return
      }

      try {
        await this.$refs.video.pause()
      } catch (error) {
        cnvs.ReportError('Failed to pause video', error)
      }
    },

    /**
     * Handle time update on video.
     */
    handleVideoTimeUpdate() {
      this.time = this.$refs.video?.currentTime || 0
      this.state = 'playing'
    },

    /**
     * If the content card has a video selected to render, set an intersection
     * observer to handle play/pausing it when not in view.
     */
    async setVideoIntersectionObserver() {
      const element = this.$refs.videoPlayer

      if (!element) {
        return
      }

      if (this.observer) {
        this.observer.disconnect()
      }

      await this.$nextTick()
      this.observer = setIntersectionObserver({
        callback: this.handleComponentIntersection,
        element,
        threshold: 0.1,
      })
    },

    /**
     * Handle component intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleComponentIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        this.handleAutoplay()
      })
    },

    /**
     * Set ready state if not already playing
     */
    setReadyState() {
      if (this.state === 'playing') {
        return
      }
      this.state = 'ready'
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
